import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import Cookies from "js-cookie";
import axios from "axios";

import { setCloudLogoAtom, maintenanceModeAtom } from "../../atoms/GlobalAtoms";
import { restoringBackupAtom } from "../../atoms/GlobalAtoms";
import { queriesEnabledAtom } from "../../atoms/GlobalAtoms";
import useLogout from "../../api/api-hooks/auth/useLogout";
import useInitializeSession from "./useInitializeSession";
import { tenantIDAtom } from "../../atoms/GlobalAtoms";
import { socketAtom } from "../../atoms/GlobalAtoms";
import useAutoRefresh from "./useAutoRefresh";
import { refreshToken } from "./refreshToken";

interface Props {
  disabled?: boolean;
  children: React.ReactNode;
}

// Global State manager focused on the current user and permissions
const UserContext = ({ children, disabled }: Props) => {
  const [queriesEnabled, setQueriesEnabled] = useAtom(queriesEnabledAtom);
  const { logout } = useLogout();
  const { tenantID: urlTenantID } = useParams();

  const tID = useAtomValue(tenantIDAtom);
  const setRestoring = useSetAtom(restoringBackupAtom);
  const setMaintenanceModeAtom = useSetAtom(maintenanceModeAtom);
  const socket = useAtomValue(socketAtom);
  const setCloudLogo = useSetAtom(setCloudLogoAtom);

  const getRefreshTokenRef = useRef<
    (useRedirect?: boolean) => Promise<any> | null
  >(() => null);
  const queriesEnabledRef = useRef<boolean>();
  const setCloudLogoRef =
    useRef<({}: { tenantID: string; value: string }) => void>();
  const isFirstRenderRef = useRef<boolean>(true);
  queriesEnabledRef.current = queriesEnabled;
  setCloudLogoRef.current = setCloudLogo;

  const getRefreshToken = (useRedirect?: boolean, extraTenantID?: string) => {
    if (disabled) return null;

    let tenantID = extraTenantID || tID || urlTenantID;

    return refreshToken(
      tenantID,
      () => logout({ useRedirect }),
      setCloudLogoRef.current,
      () => {
        queriesEnabledRef.current = true;
        setQueriesEnabled(true);
      }
    );
  };

  getRefreshTokenRef.current = getRefreshToken;

  const { initializeSession } = useInitializeSession(getRefreshTokenRef);
  useAutoRefresh(getRefreshTokenRef);

  // Fetch Refresh token on first render
  useEffect(() => {
    if (disabled) return;

    if (Cookies.get("authenticationToken")) {
      initializeSession();
    }
  }, []);

  if (disabled) return children;

  axios.interceptors.request.use((value) => {
    if (!socket?.id) return value;

    value.headers["socket-id"] = socket.id;

    return value;
  });

  // Axios response interceptor. https://github.com/axios/axios#interceptors
  axios.interceptors.response.use(
    (response) => {
      // Any status code that lies within the range of 2xx will cause this function to trigger.
      return response;
    },
    (err) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.response?.data;

      // Any status code that falls outside the range of 2xx will cause this function to trigger.
      // Handle Unauthorized 401 Response
      if (
        status === 401 &&
        err?.config?.url?.startsWith?.(window.GC_API_URL) &&
        (queriesEnabledRef.current || isFirstRenderRef.current)
      ) {
        queriesEnabledRef.current = false;
        setQueriesEnabled(false);
        // Use refresh token, and get authentication token for current tenant
        getRefreshTokenRef.current(message === "Unauthorized");
      }

      // Show restoring backup modal on this exact error
      if (status === 403 && message === "Backup in progress") {
        setRestoring(true);
      }

      if (status === 503 && message === "GrowthCloud is under maintenance.") {
        const maintananceData = err.response.data.data;
        setMaintenanceModeAtom(maintananceData);
      }

      return Promise.reject(err);
    }
  );

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
  }, []);

  return children;
};

export default UserContext;
